import { appStore } from '@kaltura-ott/tvpil-shared';

import { NETWORK_ERROR_FOR_DISPLAY, NO_CONNECTION_MESSAGE, PLEASE_CHECK_CONNECTION_MESSAGE } from 'consts';
import { CommonError, ErrorContextPropsValue } from 'context/error/types';
import networkService from 'services/networkService';

import { checkApiErrorHasRebootError } from './checkApiErrorHasRebootError';
import { isAuthError } from './checkErrorActionName';
import { checkTvpilErrorSuccessfullyCleared } from './checkTvpilErrorSuccessfullyCleared';
import { getLoginErrorTranslationId } from './getLoginErrorTranslationId';

type ErrorParamsProps = {
  commonError: CommonError;
  stopLoader(): void;
  isNoConnection: boolean;
  isErrorFromTvpil?: boolean;
  isRhinoError?: boolean;
  id?: number;
  extraActionOnRetry?: () => Promise<void> | undefined;
};

export function getErrorParams({
  commonError: { message, onRetry, title, actionName = '', code, onCancel },
  stopLoader,
  isNoConnection,
  isErrorFromTvpil,
  isRhinoError = false,
  id,
  extraActionOnRetry
}: ErrorParamsProps): ErrorContextPropsValue {
  const isAuthErr = isAuthError(actionName) && !isNoConnection;
  let icon = isNoConnection ? 'noConnection' : '';
  const errorType = isNoConnection ? 'noConnectionError' : 'serverError';
  const errorCodeForDisplay = isNoConnection ? NETWORK_ERROR_FOR_DISPLAY : code;
  let titleId = `AppErrorReaction.title.${errorType}`;
  let titleDefault = isNoConnection ? NO_CONNECTION_MESSAGE : 'Server Error';
  let messageId = `AppErrorReaction.description.${errorType}`;
  let messageDefault = isNoConnection
    ? PLEASE_CHECK_CONNECTION_MESSAGE
    : 'Something went wrong, please try again in few minutes';

  if (isAuthErr) {
    const { translationKey, translationId } = getLoginErrorTranslationId(actionName, code);
    icon = '';
    titleId = `${translationKey}.title`;
    titleDefault = title;
    messageId = translationId;
    messageDefault = message;
  }

  const resolveOnRetry = async () => {
    const hasNetwork = await networkService.isNetworkConnection();

    await onRetry();

    const isErrFromTvpilCleared = checkTvpilErrorSuccessfullyCleared(id);

    // if refetch success cleanup Rhino error
    if (isRhinoError && hasNetwork && onCancel) {
      networkService.setNetworkConnection(true);
      onCancel();
    }
    // if onRetry success cleanup Tvpil error
    if (isErrorFromTvpil && isErrFromTvpilCleared) {
      appStore.clearAllApiError();
    }

    if (isNoConnection) {
      setTimeout(() => stopLoader(), 1000);
      if (hasNetwork) {
        await appStore.restoreAfterConnectionError();

        if (extraActionOnRetry) {
          extraActionOnRetry();
        }
      }
    } else {
      stopLoader();
    }

  };

  const resolveOnClear = () => {
    const shouldRebootCalled = checkApiErrorHasRebootError();

    if (isNoConnection) {
      appStore.restoreAfterConnectionError();
    }

    if (shouldRebootCalled) {
      appStore.rebootApp(true); // to rerun app after changing route in a front of error context
    }

    appStore.clearAllApiError();
  };

  return {
    error: {
      titleId,
      titleDefault,
      messageId,
      messageDefault,
      code: errorCodeForDisplay,
      icon,
    },
    retryButton: {
      onRetry: resolveOnRetry,
      translationId: `AppErrorReaction.retry`,
      defaultMessage: 'Try again',
    },
    goBackButton: isAuthErr
      ? {
        defaultMessage: 'Go to Sign in',
        translationId: `AppErrorReaction.goBackToLogin`,
      }
      : undefined,
    onClear: resolveOnClear,
    isErrorFromTvpil,
    isRhinoError,
  };
}
